<script setup lang="ts" generic="TColumn">
import { AccordionHeader as PAccordionHeader } from 'primevue';
import {
  ref,
  toRef,
} from 'vue';

import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import VFormCheckbox from '@modules/forms/components/inputs/VFormCheckbox/VFormCheckbox.vue';
import VTableEmptyDisplay from '@modules/tables/components/VTableEmptyDisplay/VTableEmptyDisplay.vue';
import VTableFilter from '@modules/tables/components/VTableFilter/VTableFilter.vue';
import VTableMultiselectActions from '@modules/tables/components/VTableMultiselectActions/VTableMultiselectActions.vue';
import VTablePagination from '@modules/tables/components/VTablePagination/VTablePagination.vue';
import VTableTitle from '@modules/tables/components/VTableTitle/VTableTitle.vue';
import { useTable } from '@modules/tables/composables';
import { QA_TABLES } from '@modules/tables/constants';
import {
  ensureBaseRecord,
  isBaseRecordTypeGuard,
  localeKeyTypeGuard,
  paginationTypeGuard,
} from '@modules/tables/utils';

import type {
  ITableEmits,
  ITableProps,
  ITableSlots,
} from '../VTable/types';
import VTableMobileOrder from '../VTableMobileOrder/VTableMobileOrder.vue';
import VTableMobilePreviewColumns from '../VTableMobilePreviewColumns/VTableMobilePreviewColumns.vue';
import VTableMobileRows from '../VTableMobileRows/VTableMobileRows.vue';
import VTableMobileSearch from '../VTableMobileSearch/VTableMobileSearch.vue';
import VTableRefresh from '../VTableRefresh/VTableRefresh.vue';
import { useTableMobile } from './composables';

const props = withDefaults(defineProps<ITableProps<TColumn>>(), {
  rowsOnPage: 10,
  multiselect: () => ({}),
  state: () => ({
    page: 1,
  }),
  selectedIds: () => ([]),
});

const emit = defineEmits<ITableEmits>();

defineSlots<ITableSlots<TColumn>>();

const {
  selection,
  isMultiselectEnabled,
  columns,
  list,
  totalColumns,
  dataIsEmpty,
  selectionIdsOnPage,
  filterColumnOptions,
  isSearchEnabled,
  handleMultiselectAction,
  handlePagination,
  handleSearch,
  handleOrder,
  applyFilter,
  clearFilter,
  onSelectMobileRow,
  onSelectedAllMobileRows,
} = useTable(
  toRef(props, 'data'),
  toRef(props, 'state'),
  toRef(props, 'multiselect'),
  toRef(props, 'selectedIds'),
  toRef(props, 'columnsOptions'),
  ref(false),
  toRef(props, 'isFetching'),
  emit,
);

const {
  activeRow,
  isExpandedAll,
  previewColumnsStorage,
  previewColumns,
  isMultiplePreviewColumns,
  isAllSelected,
  getAccordionPanelPT,
  getAccordionHeaderPT,
  getAccordionContentPT,
  toggleAll,
  isActiveTab,
  isRowSelected,
  onPreviewColumnUpdate,
} = useTableMobile(
  columns,
  list,
  selection,
  selectionIdsOnPage,
);
</script>

<template>
  <div v-data-qa-auto-id="'VTableMobile'" v-data-test="{ el: QA_TABLES.root }">
    <div
      class="flex justify-center p-3"
      :class="{ 'justify-between': $slots.searchFilter }"
    >
      <VTableTitle :title="titleTPath" />
      <div v-if="$slots.searchFilter">
        <slot
          :table-state="state"
          name="searchFilter"
        />
      </div>
    </div>
    <div class="px-3">
      <slot
        :table-state="state"
        name="tabs"
      />
    </div>
    <div
      v-if="data"
      v-data-test="{ el: QA_TABLES.headRow }"
      class="sticky top-header-height z-10 flex min-h-[50px] items-center justify-between gap-3 bg-white py-2 pl-5 pr-1.5 shadow-md"
    >
      <div class="flex items-center gap-2">
        <VFormCheckbox
          v-if="isMultiselectEnabled"
          v-data-test="{ el: QA_TABLES.headCheckboxAll }"
          :value="isAllSelected"
          hide-errors
          class="flex pr-2"
          @change="onSelectedAllMobileRows"
        />
        <VTableMultiselectActions
          v-if="isMultiselectEnabled"
          :options="multiselect"
          :disabled="dataIsEmpty || disableAllControls || selectionIdsOnPage?.length === 0"
          static
          @select="handleMultiselectAction"
        />
        <VTableMobileOrder
          v-if="!disableSort"
          :columns="columns"
          :disabled="dataIsEmpty || loading || disableAllControls"
          :table-state="state"
          @order-update="handleOrder"
        />
        <VTableMobileSearch
          v-if="isSearchEnabled"
          :search-query="state.search?.keyword"
          :table-state="state"
          :disabled="disableAllControls"
          @search-update="handleSearch"
        />
        <VTableFilter
          v-if="localeKeyTypeGuard(data) && filterProps"
          :filter-set-id="state?.filter_set_id"
          :translation-path="data.locale_key"
          v-bind="filterProps"
          :column-options="filterColumnOptions"
          :disabled="disableAllControls"
          @apply-filter="applyFilter"
          @clear-filter="clearFilter"
        />
        <VTableRefresh
          v-if="data && refreshButton"
          :loading="isFetching"
          @click="$emit('refresh', state)"
        />
        <VTableMobilePreviewColumns
          :columns="columns"
          :preview-columns="previewColumnsStorage"
          @update="onPreviewColumnUpdate"
        />
      </div>
      <VIconButton
        v-if="!dataIsEmpty"
        v-data-test="{ el: QA_TABLES.mobileToggleAll }"
        :icon="`pi ${isExpandedAll ? 'pi-chevron-up' : 'pi-chevron-down'}`"
        @click="toggleAll"
      />
    </div>
    <div class="relative min-h-[300px]">
      <VLoader
        :loading="!data"
        min-height="300px"
        mode="structural"
      >
        <VTableEmptyDisplay
          v-if="dataIsEmpty"
          :empty-label-t-path="emptyLabelTPath"
        />
        <PAccordion
          v-else
          v-model:value="activeRow"
          expand-icon="pi pi-chevron-down"
          collapse-icon="pi pi-chevron-up"
          multiple
        >
          <PAccordionPanel
            v-for="(item, index) in list"
            :key="isBaseRecordTypeGuard(item) ? item.id : index"
            :pt="getAccordionPanelPT(index, isRowSelected(ensureBaseRecord(item).id))"
            :value="index"
          >
            <PAccordionHeader :pt="getAccordionHeaderPT(index, isMultiplePreviewColumns, isRowSelected(ensureBaseRecord(item).id))">
              <div
                v-if="isActiveTab(index)"
                class="flex min-h-[50px] gap-2 border-b border-transparent pl-5"
                @click.stop
              >
                <VFormCheckbox
                  v-if="data && isMultiselectEnabled"
                  v-data-test="{ el: QA_TABLES.bodyRowCheckbox }"
                  :value="isRowSelected(ensureBaseRecord(item).id)"
                  hide-errors
                  class="flex pr-2"
                  @change="(value) => onSelectMobileRow(ensureBaseRecord(item).id, value)"
                />
                <div
                  v-if="data && $slots.recordAction"
                  class="flex items-center justify-end gap-2"
                >
                  <slot
                    name="recordAction"
                    :table-state="state"
                    :list-item="item"
                  />
                </div>
              </div>
              <div
                v-else
                class="flex w-full"
                :class="{ 'flex-col': isMultiplePreviewColumns }"
              >
                <div
                  class="flex h-[50px] items-center gap-2 border-b"
                  :class="{ 'border-transparent': !isMultiplePreviewColumns, 'pl-5': isMultiselectEnabled || isMultiplePreviewColumns }"
                >
                  <VFormCheckbox
                    v-if="data && isMultiselectEnabled"
                    v-data-test="{ el: QA_TABLES.bodyRowCheckbox }"
                    :value="isRowSelected(ensureBaseRecord(item).id)"
                    hide-errors
                    class="pr-2"
                    @change="(value) => onSelectMobileRow(ensureBaseRecord(item).id, value)"
                  />
                  <slot
                    v-if="previewColumns?.length > 1"
                    name="recordAction"
                    :table-state="state"
                    :list-item="item"
                  />
                </div>

                <div class="flex w-full flex-col">
                  <VTableMobileRows
                    :columns="previewColumns"
                    :list-item="item"
                    :search-query="state?.search?.keyword"
                    no-border
                  />
                </div>
              </div>
            </PAccordionHeader>
            <PAccordionContent :pt="getAccordionContentPT()">
              <div v-data-test="{ el: QA_TABLES.bodyRow, rowIndex: index }">
                <VTableMobileRows
                  :columns="columns"
                  :list-item="item"
                  :search-query="state?.search?.keyword"
                />
              </div>
            </PAccordionContent>
          </PAccordionPanel>
          <PAccordionPanel
            v-if="data?.sum_columns && !dataIsEmpty"
            value="total"
            :pt="getAccordionPanelPT()"
          >
            <PAccordionHeader :pt="getAccordionHeaderPT()">
              <div
                v-data-test="{ el: QA_TABLES.bodyRowCell, columnName: 'transaction_date_for_reports' }"
                class="flex min-h-[50px] items-center gap-6 px-5 text-[15px] font-bold text-muted-color"
              >
                {{ totalLabelTPath ? $t(totalLabelTPath) : '' }}
              </div>
            </PAccordionHeader>
            <PAccordionContent :pt="getAccordionContentPT()">
              <VTableMobileRows
                :columns="totalColumns"
                :list-item="data.sum_columns"
              />
            </PAccordionContent>
          </PAccordionPanel>
        </PAccordion>
      </VLoader>
      <div class="flex min-h-[75px] flex-col content-center items-center justify-between p-4">
        <div
          v-if="!disablePagination && paginationTypeGuard(data) && rowsOnPage"
          class="mb-4"
        >
          <VTablePagination
            :start-row-num="data.start_row_num"
            :end-row-num="data.end_row_num"
            :total-pages="data.total_pages"
            :total-entries="data.total_entries"
            :rows-on-page="rowsOnPage"
            :data-is-empty="dataIsEmpty"
            :disabled="loading || disableAllControls"
            :page="state?.page || 1"
            @pagination-update="handlePagination"
          />
        </div>
        <div class="flex gap-2">
          <slot
            :table-state="state"
            name="footerButtons"
          />
        </div>
      </div>
    </div>
  </div>
</template>
